/**
 * Drives the installed-PWA Web Push lifecycle for the operational feed.
 *
 * Owns the server-persisted feed push preferences plus the browser push
 * subscription: toggling the master switch requests notification permission,
 * subscribes via the service worker, and registers the subscription with the
 * API; disabling it unsubscribes and clears the server record. Category toggles
 * are simple preference updates. Subscription is only attempted inside an
 * installed PWA (standalone), where OS push is meaningful.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { useUserOptional } from "@/auth/UserContext";
import { isStandalonePwa } from "@/lib/pwaInstall";
import {
  currentPushPermission,
  ensureSubscription,
  getExistingSubscription,
  isWebPushSupported,
  subscriptionToPayload,
} from "@/lib/webPush";
import { api, type FeedPushPreferences } from "@/services/api";

/** Category toggle keys (everything except the master `enabled` + meta). */
export type FeedPushCategoryKey =
  | "rebooks_enabled"
  | "first_books_enabled"
  | "new_bookings_enabled"
  | "batch_ingested_enabled"
  | "urgent_warning_enabled"
  | "favorites_watch_enabled"
  | "custom_events_enabled";

export interface UseFeedWebPush {
  /** Browser supports service-worker Web Push. */
  supported: boolean;
  /** Running as an installed PWA (standalone display mode). */
  standalone: boolean;
  /** Current OS notification permission. */
  permission: NotificationPermission | "unsupported";
  /** Server-persisted preferences (null until loaded). */
  prefs: FeedPushPreferences | null;
  /** Initial preference load in flight. */
  loading: boolean;
  /** A subscribe/unsubscribe/update round-trip is in flight. */
  busy: boolean;
  /** Last user-facing error, if any. */
  error: string | null;
  /** Turn the master push switch on/off (handles permission + subscription). */
  setMasterEnabled: (on: boolean) => Promise<void>;
  /** Toggle a single category preference. */
  setCategory: (key: FeedPushCategoryKey, value: boolean) => Promise<void>;
}

export function useFeedWebPush(): UseFeedWebPush {
  const userCtx = useUserOptional();
  const profile = userCtx?.profile ?? null;

  const supported = isWebPushSupported();
  const standalone = typeof window !== "undefined" && isStandalonePwa();

  const [permission, setPermission] = useState<
    NotificationPermission | "unsupported"
  >(() => currentPushPermission());
  const [prefs, setPrefs] = useState<FeedPushPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const syncedRef = useRef(false);

  // Load persisted preferences once the user profile is ready.
  useEffect(() => {
    if (!profile) return;
    let cancelled = false;
    setLoading(true);
    api
      .getFeedPushPreferences()
      .then((p) => {
        if (!cancelled) setPrefs(p);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load push preferences");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profile]);

  /** Create + register a browser subscription. Returns false on failure. */
  const registerSubscription = useCallback(async (): Promise<boolean> => {
    if (!supported) return false;
    const { key } = await api.getVapidPublicKey();
    if (!key) {
      // Server has no VAPID keypair — preference is saved but no push is sent.
      return false;
    }
    const subscription = await ensureSubscription(key);
    const payload = subscriptionToPayload(subscription);
    await api.subscribePush({ ...payload, user_agent: navigator.userAgent });
    return true;
  }, [supported]);

  /** Remove the browser subscription and its server record. */
  const removeSubscription = useCallback(async () => {
    const subscription = await getExistingSubscription();
    if (!subscription) return;
    try {
      await api.unsubscribePush(subscription.endpoint);
    } finally {
      await subscription.unsubscribe().catch(() => undefined);
    }
  }, []);

  // Best-effort re-sync: if push is enabled on an installed PWA with permission
  // already granted (e.g. a returning device), make sure a subscription exists.
  useEffect(() => {
    if (syncedRef.current) return;
    if (!prefs?.enabled || !standalone || !supported) return;
    if (currentPushPermission() !== "granted") return;
    syncedRef.current = true;
    void registerSubscription().catch(() => undefined);
  }, [prefs?.enabled, standalone, supported, registerSubscription]);

  const setMasterEnabled = useCallback(
    async (on: boolean) => {
      setError(null);
      setBusy(true);
      try {
        if (on) {
          if (!supported) {
            setError("This browser does not support push notifications.");
            return;
          }
          if (!standalone) {
            setError(
              "Install the app to your home screen to receive push notifications.",
            );
            return;
          }
          let perm = currentPushPermission();
          if (perm === "default") {
            perm = await Notification.requestPermission();
            setPermission(perm);
          }
          if (perm !== "granted") {
            setError("Notification permission was not granted.");
            return;
          }
          await registerSubscription();
          const saved = await api.updateFeedPushPreferences({
            enabled: true,
            custom_events_enabled: true,
          });
          setPrefs(saved);
        } else {
          await removeSubscription().catch(() => undefined);
          const saved = await api.updateFeedPushPreferences({ enabled: false });
          setPrefs(saved);
        }
      } catch {
        setError("Failed to update push notifications.");
      } finally {
        setBusy(false);
      }
    },
    [supported, standalone, registerSubscription, removeSubscription],
  );

  const setCategory = useCallback(
    async (key: FeedPushCategoryKey, value: boolean) => {
      setError(null);
      setBusy(true);
      try {
        const saved = await api.updateFeedPushPreferences({ [key]: value });
        setPrefs(saved);
      } catch {
        setError("Failed to update push preference.");
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  return {
    supported,
    standalone,
    permission,
    prefs,
    loading,
    busy,
    error,
    setMasterEnabled,
    setCategory,
  };
}
